import {
    BulkOperationResponse,
    ConfigurationEntryValueType,
    CredentialStoreGenericType,
    CredentialStoreResourceType,
    FeatureValues,
    FolderType,
    IAlert,
    IAsset,
    IAuditExclusion,
    IAuditLog,
    IAuditLogDetail,
    IAuthConfig,
    ICredentialStore,
    ICredentialStoreConfigurationEntry,
    ICredentialStoreDetails,
    ICredentialStoreResource,
    IEnvironment,
    IExcludedCalendar,
    IExecutionSettingConfig,
    IFeatureSetting,
    IFolder,
    IFolderAssignment,
    IFolderRole,
    IHostLicense,
    IJob,
    IJobTrigger,
    ILicense,
    ILicenseBackOffice,
    ILicenseBase,
    ILicenseFields,
    ILicenseFrontOffice,
    ILog,
    ILoginAttempt,
    IMachine,
    IMessageTemplate,
    IMLLog,
    IMLLogMetadata,
    IMLPackage,
    IMLPackageVersion,
    IMLSkill,
    IMLSkillVersion,
    IndividuallyLicensedFeature,
    IOrganizationUnit,
    IPackage,
    IPackageArgument,
    IPackageArgumentCollection,
    IPasswordChange,
    IPasswordComplexity,
    IPasswordExpired,
    IPermission,
    IProcess,
    IProcessSettingsState,
    IProcessVersion,
    IQueue,
    IQueueItem,
    IQueueItemComment,
    IQueueItemDto,
    IQueueItemEvent,
    IQueueItemIdentifier,
    IRobot,
    IRobotProvision,
    IRole,
    IRoleSource,
    ISession,
    ISessionUpdate,
    ISetting,
    ISettingsMailSmtp,
    ITask,
    ITaskAssignmentErrorResponse,
    ITaskAssignmentRequest,
    ITaskCatalog,
    ITaskCatalogRequest,
    ITenant,
    ITenantFeature,
    ITenantLicense,
    ITransaction,
    ITransactionStat,
    ITriggerGrid,
    ITriggerUpsert,
    IUser,
    IUserLoginInfo,
    IUserRole,
    IUserRoles,
    IWebhook,
    IWebhookEventTypeDto,
    JobStartStrategy,
    LicenseFrontOfficeSource,
    MessageTemplate,
    RobotHostingType,
    RobotProvisionType,
    RobotType,
    RolesAssignment,
    TriggerType,
    UserType,
} from '@models';
import { IDashboardEntry } from '@pages/analytics/generics';
import {
    IBulkAddQueueItemsFailedResponse,
    IBulkAddQueueItemsResponse,
} from '@pages/queues/modals';
import { IForm } from '@pages/tasks/form/services/form.model';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';
import {
    filterAssetResponse,
    filterIdAndKeyProperties,
    flattenDynamicProperties,
    flattenSinglePropertyArrays,
    mapJobs,
    mapPackages,
    mapProcesses,
    mapSettings,
    mapTriggers,
} from '@utils/audit.utils';
import {
    safeParse,
    safeTrim,
    toDate,
} from '@utils/object.utils';
import { isJson } from '@utils/string.utils';

import camelCase from 'lodash-es/camelCase';
import * as moment from 'moment';
import * as XRegExp from 'xregexp';

import { MapperDefinition } from './mapper.service';

type MapperCustomType<T> = (object: T) => any;

export interface MapperExplicit<T> {
    property: string | string[];
    in?: (property: any, object: T, original: any) => any;
    out?: (object: T) => any;
    isCollection?: boolean;
}

type MapperNestedDefinition<T> = [string, MapperDefinition<T>] | MapperDefinition<T>;

export type MapperDefinition<T> = {
    [Key in keyof T]: string |
    MapperCustomType<T> |
    MapperExplicit<T> |
    MapperNestedDefinition<T[Key] extends any[] ? T[Key][number] : T[Key]>;
};

const EXPIRY_NOTIFICATION_DAYS = 14;

const mapArguments = (obj: string, defaultSource?: string) => {
    if (!obj) { return; }

    const args: IPackageArgument[] = safeParse(obj);

    if (!args) { return args; }

    const inputArguments = defaultSource || JSON.stringify({});
    const parsedArguments = JSON.parse(inputArguments);

    (args || []).forEach(arg => {
        if (arg.hasDefault) {
            arg.source = 'package';
        }
        if (parsedArguments[arg.name] != null) {
            arg.source = 'process';
        }
    });

    return args;
};

const licenseCode = (code: string) => code ?
    Array(4).join('*') + code.substr(code.length - 6) :
    null;
export class MapperService {
    public static get messageTemplateMap(): MapperDefinition<IMessageTemplate> {
        return {
            id: 'Id',
            name: 'Name',
            value: 'Value',
        };
    }

    public static get robotMap(): MapperDefinition<IRobot> {
        return {
            id: 'Id',
            machineName: {
                property: 'MachineName',
                out: (obj) => safeTrim(obj.machineName),
            },
            machineId: 'MachineId',
            userId: 'UserId',
            userName: {
                property: 'Username',
                out: (obj) => safeTrim(obj.userName),
            },
            password: 'Password',
            credentialStoreId: 'CredentialStoreId',
            externalName: 'ExternalName',
            robotEnvironments: 'RobotEnvironments',
            environments: {
                property: 'Environments',
                isCollection: true,
            },
            description: {
                property: 'Description',
                out: (obj) => safeTrim(obj.description),
            },
            type: {
                property: 'Type',
                in: (prop) => RobotType[prop],
                out: (obj) => RobotType[obj.type],
            },
            hostingType: {
                property: 'HostingType',
                in: (prop) => RobotHostingType[prop],
                out: (obj) => RobotHostingType[obj.hostingType],
            },
            provisionType: {
                property: 'ProvisionType',
                in: (prop) => RobotProvisionType[prop],
                out: (obj) => RobotProvisionType[obj.provisionType],
            },
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            version: 'Version',
            computedVersion: (obj) => {
                if (!obj.version) { return null; }

                if (obj.version.lastIndexOf('.') === -1) {
                    return obj.version;
                }

                return obj.version.substring(0, obj.version.lastIndexOf('.'));
            },
            executionSettings: 'ExecutionSettings',
            license: ['License', {
                timestamp: 'Timestamp',
            }],
            isCredentialSmartCard: {
                property: 'CredentialType',
                in: (prop) => prop === 'SmartCard',
                out: (robot) => robot.isCredentialSmartCard ? 'SmartCard' : 'Default',
            },
            isExternalLicense: 'IsExternalLicensed',
        };
    }

    public static get executionSettingsMap(): MapperDefinition<IExecutionSettingConfig> {
        const castDefaultValueToProperType = (defaultValue: string, valueType: string) => {
            let castedValue;
            switch (valueType) {
                case 'MultipleChoice':
                    castedValue = defaultValue;
                    break;
                case 'Integer':
                    castedValue = parseInt(defaultValue, 10);
                    break;
                case 'Boolean':
                    castedValue = defaultValue.toLowerCase() === 'true';
            }
            return castedValue;
        };
        return {
            configuration: ['Configuration', {
                key: 'Key',
                displayName: 'DisplayName',
                valueType: 'ValueType',
                possibleValues: 'PossibleValues',
                defaultValue: {
                    property: 'DefaultValue',
                    in: (value, entry) => castDefaultValueToProperType(value, entry.valueType),
                },
            }],
        };
    }

    public static get assetMap(): MapperDefinition<IAsset> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            description: {
                property: 'Description',
                out: (obj) => safeTrim(obj.description),
            },
            value: 'Value',
            hasDefaultValue: 'HasDefaultValue',
            type: 'ValueType',
            scope: 'ValueScope',
            canBeDeleted: 'CanBeDeleted',
            stringValue: 'StringValue',
            boolValue: 'BoolValue',
            intValue: 'IntValue',
            credentialUsername: 'CredentialUsername',
            credentialPassword: 'CredentialPassword',
            externalName: 'ExternalName',
            credentialStoreId: 'CredentialStoreId',
            robotValues: ['RobotValues', {
                id: 'Id',
                robotId: 'RobotId',
                robotName: 'RobotName',
                stringValue: 'StringValue',
                boolValue: 'BoolValue',
                intValue: 'IntValue',
                type: 'ValueType',
                value: 'Value',
                credentialUsername: 'CredentialUsername',
                credentialPassword: 'CredentialPassword',
                externalName: 'ExternalName',
                credentialStoreId: 'CredentialStoreId',
            }],
        };
    }

    public static get environmentMap(): MapperDefinition<IEnvironment> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj: any) => safeTrim(obj.name),
            },
            description: {
                property: 'Description',
                out: (obj: any) => safeTrim(obj.description),
            },
        };
    }

    public static get mlPackageMap(): MapperDefinition<IMLPackage> {
        return {
            id: 'Id',
            name: 'Name',
            published: 'LastVersionUploadedOn',
            description: 'Description',
            inputType: 'InputType',
            modelVersions: ['MlPackageVersions', MapperService.mlPackageVersionMap],
            latestVersionInputDescription: 'LatestVersionInputDescription',
            latestVersionOutputDescription: 'LatestVersionOutputDescription',
            status: 'Status',
        };
    }

    public static get mlPackageVersionMap(): MapperDefinition<IMLPackageVersion> {
        return {
            id: 'Id',
            createdBy: 'Author',
            status: {
                property: 'Status',
                in: (str: string) => {
                    const lowerCase = str.toLocaleLowerCase();
                    return str.charAt(0).toUpperCase() + lowerCase.slice(1);
                },
            },
            createdOn: 'CreatedOn',
            modifiedOn: 'ModifiedOn',
            version: 'Version',
            changeLog: 'ChangeLog',
            modelLanguage: 'ModelLanguage',
            outputDescription: 'OutputDescription',
            inputType: 'InputType',
            inputDescription: 'InputDescription',
            contentUri: 'ContentUri',
            processorType: 'Processor',
        };
    }

    public static get mlSkillMap(): MapperDefinition<IMLSkill> {
        return {
            id: 'Id',
            name: 'Name',
            status: {
                property: 'Status',
                in: (str: string) => {
                    const lowerCase = str.toLowerCase();
                    return str.charAt(0).toUpperCase() + lowerCase.slice(1);
                },
            },
            processor: 'Processor',
            version: 'Version',
            predictions: 'PredictionCount',
            modelId: 'ModelId',
            currentVersion: 'CurrentVersion',
            previousVersion: 'PreviousVersion',
            description: 'Description',
            createdOn: 'CreatedOn',
            modifiedOn: 'ModifiedOn',
            inputDescription: 'InputDescription',
            outputDescription: 'OutputDescription',
            inputType: 'InputType',
        };
    }

    public static get mlSkillVersionMap(): MapperDefinition<IMLSkillVersion> {
        return {
            id: 'Id',
            current: 'Current',
            deployedOn: 'DeployedOn',
            deploymentProcessor: 'DeploymentProcessor',
            modelVersionId: 'MlPackageVersionId',
            status: {
                property: 'Status',
                in: (str: string) => {
                    const lowerCase = str.toLowerCase();
                    return str.charAt(0).toUpperCase() + lowerCase.slice(1);
                },
            },
            uploadedOn: 'UploadedOn',
            version: 'Version',
        };
    }

    public static get mlLogMetadataMap(): MapperDefinition<IMLLogMetadata> {
        return {
            packageVersion: 'MLPackageVersion',
            packageName: 'MLPackageName',
            skillName: 'MLSkillName',
            skillVersion: 'MLSkillVersion',
        };
    }

    public static get mlLogMap(): MapperDefinition<IMLLog> {
        return {
            id: 'Id',
            createdOn: 'CreatedOn',
            entityTypeName: {
                property: 'EntityTypeName',
                in: (str: string) => {
                    switch (str) {
                        case 'ML_SKILL':
                            return 'ML Skill';
                        case 'ML_PACKAGE':
                            return 'ML Package';
                        case 'ML_SERVICE':
                            return 'ML Service';
                    }

                    return str;
                },
            },
            severity: {
                property: 'Severity',
                in: (str: string) => {
                    const lowerCase = str.toLowerCase();
                    return str.charAt(0).toUpperCase() + lowerCase.slice(1);
                },
            },
            message: 'Message',
            metadata: ['Metadata', MapperService.mlLogMetadataMap],
            errorLog: 'ErrorLog',
        };
    }

    public static get webhookMap(): MapperDefinition<IWebhook> {
        return {
            id: 'Id',
            url: 'Url',
            enabled: 'Enabled',
            secret: 'Secret',
            subscribeToAllEvents: 'SubscribeToAllEvents',
            allowInsecureSSL: 'AllowInsecureSsl',
            events: {
                property: 'Events',
                in: (events) =>
                    events.map(
                        (event: { EventType: any; }) => event.EventType,
                    ),
                out: (webhook) =>
                    webhook.events &&
                    webhook.events.map(
                        event => ({
                            'EventType': event,
                        }),
                    ),
            },
        };
    }

    public static get credentialStore(): MapperDefinition<ICredentialStore> {
        return {
            id: 'Id',
            name: 'Name',
            type: {
                property: 'Type',
                in: (value) => ({
                    value,
                    genericType: value === 'Database' ? CredentialStoreGenericType.Database : CredentialStoreGenericType.SecureStore,
                }),
                out: (credentialStore) => credentialStore.type.value,
            },
            additionalConfiguration: {
                property: 'AdditionalConfiguration',
                in: (value: any) => {
                    return safeParse(value, {});
                },
                out: (credentialStore: ICredentialStore) => JSON.stringify(credentialStore.additionalConfiguration),
            },
            details: ['Details', MapperService.credentialStoreDetailsMap],
        };
    }

    public static get credentialStoreDetailsMap(): MapperDefinition<ICredentialStoreDetails> {
        return {
            isReadOnly: 'IsReadOnly',
        };
    }

    public static get credentialTypeResource(): MapperDefinition<ICredentialStoreResource> {
        return {
            id: 'Id',
            name: 'Name',
            type: {
                property: 'Type',
                in: (value) => CredentialStoreResourceType[value],
            },
        };
    }

    public static get credentialStoreConfigurationEntryBasicMap(): MapperDefinition<ICredentialStoreConfigurationEntry> {
        return {
            key: 'Key',
            displayName: 'DisplayName',
            isMandatory: 'IsMandatory',
            possibleValues: 'PossibleValues',
            defaultValueAsString: 'DefaultValue',
            valueType: 'ValueType',
            defaultValue: (entry: ICredentialStoreConfigurationEntry) => {
                if (typeof (entry.defaultValueAsString) === 'string') {
                    switch (entry.valueType) {
                        case ConfigurationEntryValueType.Boolean:
                            return entry.defaultValueAsString!.toLocaleLowerCase() === 'true';
                        case ConfigurationEntryValueType.Number:
                            return parseInt(entry.defaultValueAsString!, 10);
                        default:
                    }
                }

                return entry.defaultValueAsString;
            },
        };
    }

    public static get credentialStoreConfigurationEntryMap(): MapperDefinition<ICredentialStoreConfigurationEntry> {
        return {
            configurations: ['Configurations', MapperService.credentialStoreConfigurationEntryBasicMap],
            ...MapperService.credentialStoreConfigurationEntryBasicMap,
        };
    }

    public static get webhookEventTypeMap(): MapperDefinition<IWebhookEventTypeDto> {
        return {
            name: 'Name',
            group: 'Group',
        };
    }

    public static get auditMap(): MapperDefinition<IAuditLog> {
        const detailExclusions: IAuditExclusion = {};

        return {
            id: 'Id',
            serviceName: 'ServiceName',
            methodName: 'MethodName',
            time: 'ExecutionTime',
            parameters: 'Parameters',
            component: 'Component',
            action: 'Action',
            operation: 'OperationText',
            displayName: 'DisplayName',
            userName: 'UserName',
            userType: 'UserType',
            userId: 'UserId',
            userIsDeleted: 'UserIsDeleted',
            entityId: 'EntityId',
            data: (entry: IAuditLog) => {
                const data = safeParse(entry.parameters, {});
                const dtoKey = Object.keys(data).filter(k => k.endsWith('Dto'))[0];
                return dtoKey ? data[dtoKey] : {};
            },
            hasDetails: (entry: IAuditLog) => {
                if (!detailExclusions[entry.component]) { return true; }
                if (detailExclusions[entry.component][entry.action]) {
                    for (const key in detailExclusions[entry.component][entry.action]) {
                        if (entry.data[key] === detailExclusions[entry.component][entry.action][key]) {
                            return false;
                        }
                    }
                }
                return true;
            },
        };
    }

    public static get auditLogDetails(): MapperDefinition<IAuditLogDetail> {
        return {
            action: 'Action',
            entityName: 'EntityName',
            entityId: 'EntityId',
            customData: 'CustomData',
            data: (entry) => {
                const data = safeParse(entry.customData, {});
                data.Values = toDate(data.Values);
                data.OldValues = toDate(data.OldValues);

                if (['Create', 'Delete'].includes(entry.action)) {
                    data.OldValues = null;
                }

                flattenDynamicProperties(data.Values);
                flattenDynamicProperties(data.OldValues);
                filterIdAndKeyProperties(data.Values);
                filterIdAndKeyProperties(data.OldValues);
                filterAssetResponse(entry, data);
                flattenSinglePropertyArrays(data.Values);
                flattenSinglePropertyArrays(data.OldValues);
                mapSettings(entry, data, MapperService.getInverseSettingsMap());
                mapJobs(entry, data.Values);
                mapJobs(entry, data.OldValues);
                mapTriggers(entry, data.Values);
                mapTriggers(entry, data.OldValues);
                mapProcesses(entry, data.Values);
                mapProcesses(entry, data.OldValues);
                mapPackages(entry, data.Values);
                mapPackages(entry, data.OldValues);

                return {
                    value: data.Values,
                    oldValue: data.OldValues,
                };
            },
        };
    }

    public static get logMap(): MapperDefinition<ILog> {
        return {
            id: 'Id',
            time: 'TimeStamp',
            level: 'Level',
            processName: 'ProcessName',
            user: 'WindowsIdentity',
            message: 'Message',
            rawMessage: 'RawMessage',
            robotName: 'RobotName',
            jobKey: 'JobKey',
            machineId: 'MachineId',
            machineName: log => isJson(log.rawMessage) ? JSON.parse(log.rawMessage).machineName : '',
        };
    }

    public static get loginAttemptsMap(): MapperDefinition<ILoginAttempt> {
        return {
            id: 'Id',
            time: 'CreationTime',
            userId: 'UserId',
            browserInfo: 'BrowserInfo',
            result: 'Result',
        };
    }

    public static get packageMap(): MapperDefinition<IPackage> {
        return {
            id: 'Id',
            key: 'Key',
            description: 'Description',
            version: 'Version',
            published: 'Published',
            isActive: 'IsActive',
            isLatestVersion: 'IsLatestVersion',
            releaseNotes: 'ReleaseNotes',
            authors: 'Authors',
            ...MapperService.ioPackageArgumentMap,
        };
    }

    public static get ioPackageArgumentMap(): MapperDefinition<{ arguments: IPackageArgumentCollection }> {
        return {
            arguments: {
                property: 'Arguments',
                in: (args, _, original) => {
                    if (args == null) { args = {}; }
                    return {
                        input: mapArguments(args['Input'], original['InputArguments']),
                        output: mapArguments(args['Output']),
                    };
                },
            },
        };
    }

    public static get processSettingsMap(): MapperDefinition<{ processSettings: IProcessSettingsState }> {
        return {
            processSettings: {
                property: 'ProcessSettings',
                in: (obj) => ({
                    enabled: obj && obj.ErrorRecordingEnabled,
                    duration: obj && obj.Duration,
                    frequency: obj && obj.Frequency,
                    quality: obj && obj.Quality,
                    autoStartProcess: obj && obj.AutoStartProcess,
                }),
                out: obj => obj.processSettings ? ({
                    ErrorRecordingEnabled: obj.processSettings.enabled,
                    Duration: obj.processSettings.duration,
                    Frequency: obj.processSettings.frequency,
                    Quality: obj.processSettings.quality,
                    AutoStartProcess: obj.processSettings.autoStartProcess,
                }) : undefined,
            },
        };
    }

    public static get queueMap(): MapperDefinition<IQueue> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            description: {
                property: 'Description',
                out: (obj) => safeTrim(obj.description),
            },
            maxNoOfRetries: 'MaxNumberOfRetries',
            acceptAutoRetry: 'AcceptAutomaticallyRetry',
            enforceUniqueReference: 'EnforceUniqueReference',
            triggerId: 'ProcessScheduleId',
            processId: 'ReleaseId',
            processName: 'ReleaseName',
            specificDataJsonSchema: 'SpecificDataJsonSchema',
            outputDataJsonSchema: 'OutputDataJsonSchema',
            analyticsDataJsonSchema: 'AnalyticsDataJsonSchema',
            slaMinutes: 'SlaInMinutes',
            riskSlaMinutes: 'RiskSlaInMinutes',
            releaseId: 'ReleaseId',
        };
    }

    public static get queueItemMap(): MapperDefinition<IQueueItem> {
        return {
            id: 'Id',
            key: 'Key',
            reference: 'Reference',
            priority: 'Priority',
            deferDate: 'DeferDate',
            dueDate: 'DueDate',
            queueDefinitionId: 'QueueDefinitionId',
            status: 'Status',
            processingExceptionId: 'ProcessingExceptionId',
            processingExceptionType: 'ProcessingExceptionType',
            processingException: ['ProcessingException', {
                reason: 'Reason',
                details: 'Details',
                type: 'Type',
                id: 'Id',
            }],
            robot: ['Robot', MapperService.robotMap],
            reviewerUser: ['ReviewerUser', MapperService.userMap],
            startProcessing: 'StartProcessing',
            endProcessing: 'EndProcessing',
            anchestorId: 'AncestorId',
            retryNumber: 'RetryNumber',
            creationTime: 'CreationTime',
            specificContent: 'SpecificContent',
            output: 'Output',
            analytics: 'Analytics',
            reviewStatus: 'ReviewStatus',
            progress: 'Progress',
            rowVersion: 'RowVersion',
            queueDefinition: ['QueueDefinition', MapperService.queueMap],
        };
    }

    public static get queueItemDtoMap(): MapperDefinition<IQueueItemDto> {
        return {
            name: 'Name',
            priority: 'Priority',
            specificContent: 'SpecificContent',
            deferDate: 'DeferDate',
            dueDate: 'DueDate',
            reference: 'Reference',
            progress: 'Progress',
        };
    }

    public static get bulkQueueItemResponseMap(): MapperDefinition<IBulkAddQueueItemsResponse> {
        return {
            success: 'Success',
            message: 'Message',
            failedItems: ['FailedItems', MapperService.bulkQueueItemFailedResponseMap],
        };
    }

    public static get bulkQueueItemFailedResponseMap(): MapperDefinition<IBulkAddQueueItemsFailedResponse> {
        return {
            row: 'Ordinal',
            errorCode: 'ErrorCode',
            errorMessage: 'ErrorMessage',
        };
    }

    public static get queueItemCommentMap(): MapperDefinition<IQueueItemComment> {
        return {
            id: 'Id',
            queueItemId: 'QueueItemId',
            creationTime: 'CreationTime',
            userId: 'UserId',
            userName: 'UserName',
            text: 'Text',
        };
    }

    public static get queueItemEventMap(): MapperDefinition<IQueueItemEvent> {
        return {
            id: 'Id',
            queueItemId: 'QueueItemId',
            timestamp: 'Timestamp',
            action: 'Action',
            data: 'Data',
            userId: 'UserId',
            userName: 'UserName',
            status: 'Status',
            reviewStatus: 'ReviewStatus',
            reviewerUserId: 'ReviewerUserId',
            reviewerUserName: 'ReviewerUserName',
        };
    }

    public static get processVersionMap(): MapperDefinition<IProcessVersion> {
        return {
            id: 'Id',
            versionNumber: 'VersionNumber',
            creationTime: 'CreationTime',
        };
    }

    public static get processMap(): MapperDefinition<IProcess> {
        return {
            id: 'Id',
            key: 'Key',
            name: 'Name',
            description: {
                property: 'Description',
                out: (obj) => safeTrim(obj.description),
            },
            status: 'Status',
            environmentName: 'EnvironmentName',
            environmentId: 'EnvironmentId',
            processKey: 'ProcessKey',
            processVersion: 'ProcessVersion',
            isLatestVersion: 'IsLatestVersion',
            isProcessDeleted: 'IsProcessDeleted',
            currentVersion: ['CurrentVersion', MapperService.processVersionMap],
            environment: ['Environment', MapperService.environmentMap],
            releaseVersions: ['ReleaseVersions', MapperService.processVersionMap],
            inputArguments: {
                property: 'InputArguments',
                in: (inputArguments: string) => safeParse(inputArguments),
                out: (entity: { inputArguments: any; }) => JSON.stringify(entity.inputArguments),
            },
            ...MapperService.ioPackageArgumentMap,
            ...MapperService.processSettingsMap,
        };
    }

    public static get userRolesMap(): MapperDefinition<IUserRoles> {
        return {
            tenantRoles: ['TenantRoles', MapperService.userRoleMap],
            folderRoles: ['PageItems', MapperService.folderRoleMap],
            count: 'Count',
        };
    }

    public static get userRoleMap(): MapperDefinition<IUserRole> {
        return {
            id: 'Id',
            name: 'Name',
            source: ['Users', MapperService.roleSourceMap],
        };
    }

    public static get roleSourceMap(): MapperDefinition<IRoleSource> {
        return {
            id: 'Id',
            username: 'UserName',
            isInherited: 'IsInherited',
        };
    }

    public static get folderRoleMap(): MapperDefinition<IFolderRole> {
        return {
            folder: ['Folder', {
                id: 'Id',
                fullyQualifiedName: 'FullyQualifiedName',
                displayName: 'DisplayName',
            }],
            roles: ['Roles', MapperService.userRoleMap],
            id: (obj) => obj.folder.id,
        };
    }

    public static get roleMap(): MapperDefinition<IRole> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            displayName: 'DisplayName',
            permissions: ['Permissions', MapperService.permissionMap],
            isEditable: 'IsEditable',
            isStatic: 'IsStatic',
        };
    }

    public static get permissionMap(): MapperDefinition<IPermission> {
        return {
            name: 'Name',
            isGranted: 'IsGranted',
            scope: 'Scope',
        };
    }

    public static get settingMap(): MapperDefinition<ISetting> {
        return {
            name: 'Name',
            value: 'Value',
            scope: 'Scope',
            id: (obj) => obj.name,
        };
    }

    public static get licenseBaseMap(): MapperDefinition<ILicenseBase> {
        return {
            expirationDate: {
                property: 'ExpireDate',
                in: (epoch: number) => new Date(new Date(0).setSeconds(epoch)),
            },
            gracePeriodEndDate: {
                property: 'GracePeriodEndDate',
                in: (epoch: number) => new Date(new Date(0).setSeconds(epoch)),
            },
            allowed: ['Allowed', MapperService.licenseFieldsMap],
            used: ['Used', MapperService.licenseFieldsMap],
            isRegistered: 'IsRegistered',
            isExpired: 'IsExpired',
            features: {
                property: 'LicensedFeatures',
                in: (obj: string[]) => {
                    const o: Partial<Record<IndividuallyLicensedFeature, boolean>> = {};
                    return !!obj ? obj.reduce((acc, curr) => { (acc as any)[curr] = true; return acc; }, o) : o;
                },
            },
            developmentConcurrent: 'DevelopmentConcurrent',
            attendedConcurrent: 'AttendedConcurrent',
            studioXConcurrent: 'StudioXConcurrent',
            hostLicenseId: 'HostLicenseId',
            code: {
                property: 'Code',
                in: licenseCode,
            },
            isCommunity: (obj) => !obj.isExpired && !obj.isRegistered,
            expiresSoon: (obj) => moment(obj.expirationDate)
                .subtract(EXPIRY_NOTIFICATION_DAYS, 'days')
                .isBefore() &&
                !obj.isExpired,
            totals: {
                property: '',
                in: (_, license) => {
                    if (!license) { return license; }

                    const totals: Record<string, number> = {};

                    Object.keys(license.allowed).forEach(key => {
                        totals[key] = Math.min(100, Math.round((license.used as any)[key] / (license.allowed as any)[key] * 100) || 0);
                    });

                    return totals;
                },
            },
        };
    }

    public static get licenseMap(): MapperDefinition<ILicense> {
        return MapperService.licenseBaseMap;
    }

    public static get licenseFieldsMap(): MapperDefinition<ILicenseFields> {
        return {
            attended: 'Attended',
            unattended: 'Unattended',
            nonProduction: 'NonProduction',
            development: 'Development',
            studioX: 'StudioX',
        };
    }

    public static get runtimeLicenseMap(): MapperDefinition<ILicenseBackOffice> {
        return {
            key: 'Key',
            machineId: 'MachineId',
            isOnline: 'IsOnline',
            isLicensed: 'IsLicensed',
            machineName: 'MachineName',
            robotsCount: 'RobotsCount',
            runtimes: 'Runtimes',
            executingCount: 'ExecutingCount',
            enabled: 'Enabled',
        };
    }

    public static get namedLicenseMap(): MapperDefinition<ILicenseFrontOffice> {
        return {
            activeRobotId: 'ActiveRobotId',
            key: 'Key',
            userName: 'UserName',
            lastLogin: 'LastLoginDate',
            machines: 'MachinesCount',
            machineNames: {
                property: 'MachineNames',
                in: machineNames => machineNames.join(', '),
            },
            activeMachineNames: 'ActiveMachineNames',
            // don't display the machine for AD users
            displayMachineNames: entry => !(entry.userName || '').includes(`\\`) ? entry.machineNames : '',
            displayMachineNamesTooltip: {
                property: 'ActiveMachineNames',
                in: activeMachineNames => activeMachineNames.join(', '),
            },
            isLicensed: 'IsLicensed',
            source: {
                property: 'IsExternalLicensed',
                in: (isExternalLicense) => isExternalLicense
                    ? LicenseFrontOfficeSource.Local
                    : LicenseFrontOfficeSource.Orchestrator,
            },
        };
    }

    public static get hostLicenseMap(): MapperDefinition<IHostLicense> {
        return {
            ...MapperService.licenseBaseMap,
            id: 'Id',
            uploadDate: 'CreationTime',
            tenantCount: 'TenantsCount',
        };
    }

    public static get tenantLicenseMap(): MapperDefinition<ITenantLicense> {
        return {
            tenantId: 'TenantId',
            hostLicenseId: 'HostLicenseId',
            attended: 'Attended',
            unattended: 'Unattended',
            nonProduction: 'NonProduction',
            development: 'Development',
            studioX: 'StudioX',
        };
    }

    public static get transactionMap(): MapperDefinition<ITransaction> {
        return {
            id: 'QueueDefinitionId',
            name: 'QueueDefinitionName',
            description: 'QueueDefinitionDescription',
            acceptAutoRetry: 'QueueDefinitionAcceptAutomaticallyRetry',
            maxNoOfRetries: 'QueueDefinitionMaxNumberOfRetries',
            enforceUniqueReference: 'QueueDefinitionEnforceUniqueReference',
            itemsToProcess: 'ItemsToProcess',
            itemsInProgress: 'ItemsInProgress',
            avgTime: 'ProcessingMeanTime',
            successfulTransactions: 'SuccessfulTransactionsNo',
            applicationExceptions: 'ApplicationExceptionsNo',
            businessExceptions: 'BusinessExceptionsNo',
            processName: 'ReleaseName',
            triggerId: 'ProcessScheduleId',
            specificDataJsonSchemaExists: 'SpecificDataJsonSchemaExists',
            outputDataJsonSchemaExists: 'OutputDataJsonSchemaExists',
            analyticsDataJsonSchemaExists: 'AnalyticsDataJsonSchemaExists',
        };
    }

    public static get transactionStatsMap(): MapperDefinition<ITransactionStat> {
        return {
            id: 'QueueDefinitionId',
            processingTime: 'ProcessingTime',
            reportType: 'ReportType',
            applicationExceptionNo: 'NumberOfApplicationExceptions',
            businessExceptionNo: 'NumberOfBusinessExceptions',
            successNo: 'NumberOfSuccessfulTransactions',
            retriedItems: 'NumberOfRetriedItems',
            appExProcTime: 'ApplicationExceptionsProcessingTime',
            businessExProcTime: 'BusinessExceptionsProcessingTime',
            successProcTime: 'SuccessfulTransactionsProcessingTime',
        };
    }

    public static get triggerGridMap(): MapperDefinition<ITriggerGrid> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            enabled: 'Enabled',
            queueDefinitionId: 'QueueDefinitionId',
            queueDefinitionName: 'QueueDefinitionName',
            packageName: 'PackageName',
            environmentName: 'EnvironmentName',

            startProcessCronSummary: 'StartProcessCronSummary',
            startProcessNextOccurrence: 'StartProcessNextOccurrence',
            stopProcessExpression: 'StopProcessExpression',
            timezoneIana: 'TimeZoneIana',
            startStrategy: 'StartStrategy',
        };
    }

    public static get triggerUpsertMap(): MapperDefinition<ITriggerUpsert> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            inputArguments: {
                property: 'InputArguments',
                in: (prop: string) => safeParse(prop) || {},
                out: (obj: { inputArguments: any; }) => JSON.stringify(obj.inputArguments),
            },
            enabled: 'Enabled',
            useCalendar: 'UseCalendar',
            calendar: {
                property: ['CalendarId'],
                in: obj => [{
                    id: obj['CalendarId'],
                    text: '',
                }],
                out: obj => ({
                    'CalendarId': obj.calendar && obj.calendar[0] ? obj.calendar[0].id : undefined,
                }),
            },
            itemsActivationThreshold: 'ItemsActivationThreshold',
            itemsPerJobActivationTarget: 'ItemsPerJobActivationTarget',
            maxJobsForActivation: 'MaxJobsForActivation',

            startProcessCronDetails: 'StartProcessCronDetails',
            startProcessCron: {
                property: 'StartProcessCron',
                out: (obj) => safeTrim(obj.startProcessCron),
            },
            environmentId: {
                property: 'EnvironmentId',
                in: (prop) => Number(prop),
            },
            executorRobots: ['ExecutorRobots', {
                id: 'Id',
            }],
            stopStrategy: 'StopStrategy',
            stopEnabled: {
                property: 'StopProcessExpression',
                in: prop => !!prop,
                out: obj => obj.stopEnabled ?
                    (obj.stopDays * 86400 + obj.stopHours * 3600 + obj.stopMinutes * 60).toString() :
                    '',
            },
            stopDays: {
                property: 'StopProcessExpression',
                in: prop => prop ?
                    Math.trunc(
                        parseInt(prop, 10) / 86400,
                    ) :
                    0,
            },
            stopHours: {
                property: 'StopProcessExpression',
                in: prop => prop ?
                    Math.trunc(
                        parseInt(prop, 10) % 86400 / 3600,
                    ) :
                    0,
            },
            stopMinutes: {
                property: 'StopProcessExpression',
                in: prop => prop ?
                    Math.trunc(
                        parseInt(prop, 10) % 3600 / 60,
                    ) :
                    0,
            },
            process: {
                property: ['ReleaseId', 'ReleaseName'],
                in: obj => [{
                    id: obj['ReleaseId'],
                    text: obj['ReleaseName'],
                }],
                out: obj => ({
                    'ReleaseId': obj.process[0].id,
                    'ReleaseName': obj.process[0].text,
                }),
            },
            queue: {
                property: ['QueueDefinitionId', 'QueueDefinitionName'],
                in: obj => [{
                    id: obj['QueueDefinitionId'],
                    text: obj['QueueDefinitionName'],
                }],
                out: obj => ({
                    'QueueDefinitionId': obj.queue ?
                        obj.queue[0].id :
                        null,
                    'QueueDefinitionName': obj.queue ?
                        obj.queue[0].text :
                        null,
                }),
            },
            timezone: {
                property: 'TimeZoneId',
                in: prop => [{
                    id: prop,
                }],
                out: obj => obj.timezone[0].id,
            },
            type: {
                property: 'QueueDefinitionId',
                in: prop => prop ? TriggerType.Queue : TriggerType.Time,
            },
            startStrategyRadio: {
                property: 'StartStrategy',
                in: prop => {
                    switch (prop) {
                        case -1:
                            return JobStartStrategy.AllRobots;
                        case 0:
                            return JobStartStrategy.SpecificRobots;
                        default:
                            return JobStartStrategy.JobsCount;
                    }
                },
            },
            startStrategy: 'StartStrategy',
            stopProcessDate: 'StopProcessDate',
            disableAfter: {
                property: 'StopProcessDate',
                in: prop => !!prop,
            },
            externalJobKey: 'ExternalJobKey',
        };
    }

    public static get sessionMap(): MapperDefinition<ISession> {
        return {
            id: 'Id',
            folder: 'FolderName',
            robot: ['Robot', MapperService.robotMap],
            state: 'State',
            reportingTime: 'ReportingTime',
            isUnresponsive: 'IsUnresponsive',
            machineId: 'MachineId',
            machineName: 'MachineName',
            info: 'Info',
            hostMachineName: 'HostMachineName',
            licenseErrorCode: 'LicenseErrorCode',
            computedState: (obj: ISession): string => {
                return obj.isUnresponsive ? 'Unresponsive' : obj.state;
            },
        };
    }

    public static get sessionUpdatedMap(): MapperDefinition<ISessionUpdate> {
        return {
            state: 'state',
            reportingTime: 'reportingTime',
            isUnresponsive: 'isUnresponsive',
            hostMachineName: 'hostMachineName',
            machineName: 'machineName',
            id: 'sessionId',
            robotId: 'robotId',
            isNew: 'isNew',
            licenseErrorCode: 'licenseErrorCode',
            computedState: (obj: any): string => {
                return obj.isUnresponsive ? 'Unresponsive' : obj.state;
            },
        };
    }

    public static get jobTriggerMap(): MapperDefinition<IJobTrigger> {
        return {
            id: 'Id',
            jobId: 'JobId',
            status: 'Status',
            triggerType: 'TriggerType',
            triggerMessage: 'TriggerMessage',
            itemId: 'ItemId',
        };
    }

    public static get jobMap(): MapperDefinition<IJob> {
        return {
            id: 'Id',
            key: 'Key',
            startTime: 'StartTime',
            endTime: 'EndTime',
            info: 'Info',
            state: 'State',
            source: 'Source',
            sourceType: 'SourceType',
            creationTime: 'CreationTime',
            robot: ['Robot', MapperService.robotMap],
            release: ['Release', MapperService.processMap],
            environmentName: {
                property: 'Release/Name',
                in: (_, obj) => {
                    if (obj && obj.release) {
                        const fullReleaseName = obj.release.name;
                        const processName = obj.release.processKey;
                        const environmentName = fullReleaseName.substring(processName.length + 1);
                        return environmentName;
                    }
                    return '';
                },
            },
            startingTriggerId: 'StartingScheduleId',
            type: 'Type',
            inputArguments: {
                property: 'InputArguments',
                in: (obj: string) => safeParse(obj),
                out: (obj: { inputArguments: any; }) => JSON.stringify(obj.inputArguments),
            },
            outputArguments: {
                property: 'OutputArguments',
                in: (obj: string) => safeParse(obj),
            },
            hostMachineName: 'HostMachineName',
            hasMediaRecorded: 'HasMediaRecorded',
        };
    }

    public static get userMap(): MapperDefinition<IUser> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            surname: {
                property: 'Surname',
                out: (obj) => safeTrim(obj.surname),
            },
            userName: 'UserName',
            domain: 'Domain',
            fullName: 'FullName',
            emailAddress: 'EmailAddress',
            isEmailConfirmed: 'IsEmailConfirmed',
            isActive: 'IsActive',
            authenticationSource: 'AuthenticationSource',
            password: 'Password',
            lastLoginDate: 'LastLoginTime',
            creationTime: 'CreationTime',
            rolesList: 'RolesList',
            organizationUnits: [
                'OrganizationUnits',
                MapperService.organizationUnitMap,
            ],
            tenancyName: 'TenancyName',
            tenantDisplayName: 'TenantDisplayName',
            tenancyId: 'TenantId',
            type: {
                property: 'Type',
                in: (prop) => UserType[prop],
                out: (obj) => UserType[obj.type],
            },
            notificationSettings: ['NotificationSubscription', {
                transactions: 'QueueItems',
                robots: 'Robots',
                jobs: 'Jobs',
                tasks: 'Tasks',
                schedules: 'Schedules',
                queues: 'Queues',
            }],
            mayHaveRobotSession: 'MayHaveRobotSession',
            mayHaveUserSession: 'MayHaveUserSession',
            bypassBasicAuthRestriction: 'BypassBasicAuthRestriction',
            robotProvision: ['RobotProvision', MapperService.robotProvisionMap],
            isExternalLicensed: 'IsExternalLicensed',
            licenseType: {
                property: 'LicenseType',
                in: (prop) => prop != null ? RobotType[prop] : prop,
                out: (obj) => obj.licenseType != null ? RobotType[obj.licenseType] : obj.licenseType,
            },
        };
    }

    public static get userLoginInfoMap(): MapperDefinition<IUserLoginInfo> {
        return {
            id: 'Id',
            userName: 'UserName',
            name: 'Name',
            surname: 'Surname',
            emailAddress: 'EmailAddress',
        };
    }

    public static get tenantMap(): MapperDefinition<ITenant> {
        return {
            id: 'Id',
            name: 'Name',
            displayName: 'DisplayName',
            adminEmailAddress: 'AdminEmailAddress',
            adminName: 'AdminName',
            adminSurname: 'AdminSurname',
            adminPassword: 'AdminPassword',
            lastLoginTime: 'LastLoginTime',
            isActive: 'IsActive',
            license: ['License', {
                id: 'Id',
                hostLicenseId: 'HostLicenseId',
                code: {
                    property: 'Code',
                    in: licenseCode,
                },
                allowed: ['Allowed', MapperService.licenseFieldsMap],
            }],
            hasConnectionString: 'HasConnectionString',
            connectionString: 'ConnectionString',
        };
    }

    public static get tenantFeatureMap(): MapperDefinition<ITenantFeature> {
        return {
            featureSettings: ['FeatureSettings', MapperService.featureSettingMap],
            tenantId: 'TenantId',
        };
    }

    public static get featureSettingMap(): MapperDefinition<IFeatureSetting> {
        return {
            displayName: 'DisplayName',
            name: 'Name',
            value: {
                property: 'Value',
                in: (featureValue: string) => (featureValue === FeatureValues.True),
            },
            notEditable: 'NotEditable',
            tooltip: 'Tooltip',
        };
    }

    public static get alertMap(): MapperDefinition<IAlert> {
        return {
            id: 'Id',
            name: 'NotificationName',
            component: {
                property: 'Component',
                in: (component) =>
                    component === 'Schedules'
                        ? 'Triggers'
                        : component,
            },
            data: {
                property: 'Data',
                in: (json) => {
                    const parsed = safeParse(json, {});
                    const keys = Object.keys(parsed);

                    const mapped: Record<string, string> = {};
                    for (const key of keys) {
                        mapped[camelCase(key)] = parsed[key];
                    }

                    return mapped;
                },
            },
            showDetails: {
                property: 'Component',
                in: (component) => {
                    switch (component) {
                        case 'Transactions':
                        case 'Jobs':
                        case 'Process':
                            return true;
                        case 'Queues':
                            return false;
                        default:
                            return false;
                    }
                },
            },
            permissions: {
                property: 'Component',
                in: (component) => {
                    switch (component) {
                        case 'Transactions':
                            return ['Transactions.View', 'Queues.View'];
                        case 'Jobs':
                            return ['Jobs.View'];
                        case 'Process':
                        case 'Queues':
                            return ['Alerts.View'];
                    }
                },
            },
            severity: 'Severity',
            timestamp: 'CreationTime',
            state: 'State',
            userNotificationId: 'UserNotificationId',
        };
    }

    public static get organizationUnitMap(): MapperDefinition<IOrganizationUnit> {
        return {
            id: 'Id',
            displayName: {
                property: 'DisplayName',
                out: (obj) => safeTrim(obj.displayName),
            },
        };
    }

    public static get foldersMap(): MapperDefinition<IFolder> {
        return {
            id: 'Id',
            displayName: 'DisplayName',
            description: 'Description',
            hasChildren: 'HasChildren',
            rolesAssignment: {
                property: 'PermissionModel',
                in: obj => obj === 'FineGrained' ? RolesAssignment.CustomRoles : RolesAssignment.InheritFromTenant,
                out: obj => !!obj.rolesAssignment
                    ? obj.rolesAssignment === RolesAssignment.CustomRoles ? 'FineGrained' : 'InheritFromTenant'
                    : undefined,
            },
            isSelectable: 'IsSelectable',
            type: {
                property: 'ProvisionType',
                in: obj => obj === 'Automatic' ? FolderType.Modern : FolderType.Classic,
                out: obj => !!obj.type
                    ? obj.type === FolderType.Modern ? 'Automatic' : 'Manual'
                    : undefined,
            },
            fullyQualifiedName: 'FullyQualifiedName',
            parentId: 'ParentId',
            level: {
                property: 'Level',
                in: level => level + 1,
            },
        };
    }

    public static get folderAssignmentsMap(): MapperDefinition<IFolderAssignment> {
        return {
            id: 'Id',
            user: ['UserEntity', {
                id: 'Id',
                userName: 'UserName',
                isInherited: 'IsInherited',
            }],
            roles: ['Roles', {
                id: 'Id',
                name: 'Name',
                origin: 'Origin',
                inheritedFromFolder: ['InheritedFromFolder', {
                    id: 'Id',
                    fullyQualifiedName: 'FullyQualifiedName',
                    displayName: {
                        property: 'DisplayName',
                        out: (obj) => safeTrim(obj.displayName),
                    },
                }],
            }],
        };
    }

    public static get bulkOperationMap(): MapperDefinition<BulkOperationResponse> {
        return {
            success: 'Success',
            message: 'Message',
            failedItems: 'FailedItems',
        };
    }

    public static get queueItemIdentifierMap(): MapperDefinition<IQueueItemIdentifier> {
        return {
            id: 'Id',
            rowVersion: 'RowVersion',
        };
    }

    public static get changePasswordMap(): MapperDefinition<IPasswordChange> {
        return {
            currentPassword: 'CurrentPassword',
            newPassword: 'NewPassword',
        };
    }

    public static get machineMap(): MapperDefinition<IMachine> {
        return {
            id: 'Id',
            name: {
                property: 'Name',
                out: (obj) => safeTrim(obj.name),
            },
            type: 'Type',
            robotVersions: {
                property: 'RobotVersions',
                in: (robotVersions) => {
                    if (!robotVersions) { return []; }

                    const versions = robotVersions.map((item: any) => ({
                        count: item.Count,
                        version: item.Version && item.Version.substring(
                            0,
                            item.Version.lastIndexOf('.') !== -1 ?
                                item.Version.lastIndexOf('.') :
                                item.Version.length,
                        ),
                    }));
                    return versions.reduce((acc: any[], curr: { version: any; count: string | number; }) => {
                        const existing = acc.find(robot => robot.version === curr.version);
                        if (existing) {
                            existing.count += curr.count;
                        } else {
                            acc.push(curr);
                        }
                        return acc;
                    }, []);
                },
            },
            licenseKey: 'LicenseKey',
            nonProductionSlots: 'NonProductionSlots',
            unattendedSlots: 'UnattendedSlots',
            description: 'Description',
        };
    }

    public static get changeExpiredPasswordMap(): MapperDefinition<IPasswordExpired> {
        return {
            ...MapperService.changePasswordMap,
            userId: 'UserId',
        };
    }

    public static settingsMappings = {
        mail: {
            alertsEmailEnabled: 'Alerts.Email.Enabled',
            smtpHost: 'Abp.Net.Mail.Smtp.Host',
            smtpPort: 'Abp.Net.Mail.Smtp.Port',
            smtpDomain: 'Abp.Net.Mail.Smtp.Domain',
            smtpUsername: 'Abp.Net.Mail.Smtp.UserName',
            smtpPassword: 'Abp.Net.Mail.Smtp.Password',
            smtpUseDefaultCredentials: 'Abp.Net.Mail.Smtp.UseDefaultCredentials',
            smtpEnableSsl: 'Abp.Net.Mail.Smtp.EnableSsl',
            defaultFromAddress: 'Abp.Net.Mail.DefaultFromAddress',
            defaultFromDisplayName: 'Abp.Net.Mail.DefaultFromDisplayName',
        },
        general: {
            timezone: 'Abp.Timing.TimeZone',
            language: 'Abp.Localization.DefaultLanguageName',
            isModernFolderSystemEnabled: 'Features.ModernFolders.Enabled',
        },
        security: {
            passwordComplexity: 'PasswordComplexity',
            complexity: {
                hasDigits: 'HasDigit',
                hasLowerCase: 'HasLowercase',
                hasSpecialCharacter: 'HasSpecialCharacter',
                hasUpperCase: 'HasUppercase',
                length: 'Length',
                regex: 'RegexComplexity',
            },
            password: {
                defaultExpirationDays: 'Auth.Password.DefaultExpirationDays',
                previousUseLimit: 'Auth.Password.PreviousUseLimit',
                shouldChangePasswordAfterFirstLogin: 'Auth.Password.ShouldChangePasswordAfterFirstLogin',
            },
            lockout: {
                isEnabled: 'Auth.UserLockOut.IsEnabled',
                maxAccessAttempts: 'Auth.UserLockOut.MaxFailedAccessAttemptsBeforeLockout',
                defaultAccountLockoutSeconds: 'Auth.UserLockOut.DefaultAccountLockoutSeconds',
            },
            robot: {
                runDisconnected: 'AttendedRobot.RunDisconnectedHours',
            },
        },
        scalability: {
            signalREnabled: 'Scalability.SignalR.Enabled',
            signalRTransport: 'Scalability.SignalR.Transport',
        },
        deployment: {
            packages: {
                isInternalDeployment: 'InternalDeployment',
                deploymentUrl: 'DeploymentUrl',
                authenticationType: 'Deployment.Processes.AuthenticationType',
                apiKey: 'NuGet.Packages.ApiKey',
                basicAuthUsername: 'DeploymentBasicUsername',
                basicAuthPassword: 'DeploymentBasicPassword',
            },
            libraries: {
                useSharedFeed: 'Deployment.Libraries.UseSharedFeed',
                allowTenantPublish: 'Deployment.Libraries.AllowTenantPublish',
                isInternalDeployment: 'Deployment.Libraries.Internal',
                deploymentUrl: 'Deployment.Libraries.Url',
                authenticationType: 'Deployment.Libraries.AuthenticationType',
                apiKey: 'NuGet.Activities.ApiKey',
            },
        },
        application: {
            termsAndConditionsRegisterUrl: 'TermsAndConditionsRegisterUrl',
            isTenantRegistrationEnabled: 'Tenant.Registration.Enabled',
            customTitle: 'CustomTitle',
        },
    };

    public static get robotProvisionMap(): MapperDefinition<IRobotProvision> {
        return {
            isExternalLicensed: 'IsExternalLicensed',
            userName: 'UserName',
            executionSettings: 'ExecutionSettings',
            type: {
                property: 'RobotType',
                in: (prop) => prop != null ? RobotType[prop] : prop,
                out: (obj) => obj.type != null ? RobotType[obj.type] : obj.type,
            },
        };
    }

    public static authSettingsMapper(settings: any): IAuthConfig {
        const getProp = (prop: string) => {
            const idx = settings.Keys.indexOf(prop);
            return settings.Values[idx];
        };

        return {
            serverSettings: {
                path: getProp('Application.Path'),
                build: {
                    version: getProp('Build.Version'),
                    date: getProp('Build.Date'),
                },
                baseTitle: getProp('Application.Title'),
                year: getProp('Application.Year'),
            },
            passwordComplexity: this.passwordComplexityMapper(getProp('PasswordComplexity')),
            application: {
                isTenantRegistrationEnabled: getProp('Tenant.Registration.Enabled') === 'true',
                termsAndConditionsRegisterUrl: getProp('TermsAndConditionsRegisterUrl'),
                isLocalizationEnabled: getProp('Localization.Enabled') === 'True',
            },
            mixpanelKey: getProp('Mixpanel.Key'),
            isTelemetryEnabled: getProp('Telemetry.Enabled') === 'True',
            currentCulture: getProp('CurrentCulture'),
            loginLayout: getProp(MessageTemplate.LoginLayout),
            isRememberMeEnabled: getProp('Auth.RememberMe.Enabled') === 'True',
            cloud: {
                allowPasswordChange: getProp('Auth.AllowChangePassword') === 'True',
                allowSelfEmailUpdate: getProp('Auth.AllowSelfEmailUpdate') === 'True',
                logoutUrl: getProp('Auth.ExternalLogoutUrl'),
            },
            auth: {
                isCustomSsoEnabled: getProp('CustomSsoCookie.Enabled') === 'True',
                isGoogleAuthenticationEnabled: getProp('ExternalAuth.Google.Enabled') === 'true',
                isWindowsAuthenticationEnabled: getProp('WindowsAuth.Enabled') === 'true',
                isAzureAuthenticationEnabled: getProp('ExternalAuth.AzureAD.Enabled') === 'true',
                isSaml2AuthenticationEnabled: getProp('ExternalAuth.Saml2.Enabled') === 'true',
                isSystemOpenIdAuthenticationEnabled: getProp('ExternalAuth.System.OpenIdConnect.Enabled') === 'true',
            },
        };
    }

    public static get excludedCalendarMapper(): MapperDefinition<IExcludedCalendar> {
        return {
            calendarId: 'Id',
            calendarName: 'Name',
            timeZoneId: 'TimeZoneId',
            excludedDates: 'ExcludedDates',
            newEntry: false,
        };
    }

    public static get taskMapper(): MapperDefinition<ITask> {
        return {
            id: 'Id',
            taskPriority: 'Priority',
            title: 'Title',
            creationTime: 'CreationTime',
            status: 'Status',
            type: 'Type',
            assignedToUser: ['AssignedToUser', MapperService.userLoginInfoMap],
            taskCatalogName: 'TaskCatalogName',
            organizationUnitId: 'OrganizationUnitId',
            isCompleted: 'IsCompleted',
        };
    }

    public static get taskAssignmentRequestMap(): MapperDefinition<ITaskAssignmentRequest> {
        return {
            taskId: 'TaskId',
            userId: 'UserId',
        };
    }

    public static get taskAssignmentResponseMap(): MapperDefinition<ITaskAssignmentErrorResponse> {
        return {
            taskId: 'TaskId',
            userId: 'UserId',
            errorCode: 'ErrorCode',
            errorMessage: 'ErrorMessage',
        };
    }

    public static get taskCatalogMapper(): MapperDefinition<ITaskCatalog> {
        return {
            id: 'Id',
            name: 'Name',
            description: 'Description',
            modificationTime: 'LastModificationTime',
        };
    }

    public static get taskCatalogRequestMapper(): MapperDefinition<ITaskCatalogRequest> {
        return {
            name: 'Name',
            description: 'Description',
        };
    }

    public static get taskFormMapper(): MapperDefinition<IForm> {
        return {
            formLayout: 'formLayout',
            data: 'data',
            action: 'action',
            actionLabel: 'actionLabel',
            status: 'status',
            assignedToUserId: 'assignedToUserId',
        };
    }

    public static get smtpParametersMap(): MapperDefinition<ISettingsMailSmtp> {
        return {
            smtpHost: 'Host',
            smtpPort: 'Port',
            smtpDomain: 'Domain',
            smtpUsername: 'UserName',
            smtpPassword: 'Password',
            smtpUseDefaultCredentials: 'UseDefaultCredentials',
            smtpEnableSsl: 'EnableSsl',
            defaultFromAddress: 'DefaultFromAddress',
            defaultFromDisplayName: 'DefaultFromDisplayName',
        };
    }

    public static get dashboardEntryMap(): MapperDefinition<IDashboardEntry> {
        return {
            id: '_id',
            oid: 'oid',
            title: 'title',
            widgetCount: 'widgetCount',
            lastUpdated: 'lastUpdated',
            lastPublished: 'lastPublish',
            ownerInfo: 'ownerInfo',
        };
    }

    public static get suggestValueMap(): MapperDefinition<ISuggestValue> {
        return {
            id: 'oid',
            text: 'title',
        };
    }

    public static passwordComplexityMapper(passComplexity: string): IPasswordComplexity {
        const complexity = safeParse(passComplexity);
        const serverRegex = complexity['RegexComplexity'];
        const regexSuffix = 'Regex';
        const regex = {
            hasDigits: XRegExp(serverRegex['HasDigit' + regexSuffix]),
            hasLowerCase: XRegExp(serverRegex['HasLowercase' + regexSuffix]),
            hasSpecialCharacter: XRegExp(serverRegex['HasSpecialCharacter' + regexSuffix]),
            hasUpperCase: XRegExp(serverRegex['HasUppercase' + regexSuffix]),
        };
        return {
            hasDigits: complexity['HasDigit'],
            hasLowerCase: complexity['HasLowercase'],
            hasSpecialCharacter: complexity['HasSpecialCharacter'],
            hasUpperCase: complexity['HasUppercase'],
            length: complexity['Length'],
            regex,
        };
    }

    public static getInverseMap = (originalMap: any) => {
        const inverseMap: any = {};
        const keys = Object.keys(originalMap);
        for (const key of keys) {
            inverseMap[originalMap[key]] = key;
        }
        return inverseMap;
    }

    public static getInverseSettingsMap = () => {
        const map: any = {};
        const inverseMapping = (prefix: any[], currentMap: any) => {
            if (typeof currentMap === 'string') {
                map[currentMap] = prefix.map(p => {
                    const newKey = p[0].toUpperCase() + p.substring(1);
                    return newKey;
                }).join('.');
            } else {
                Object.keys(currentMap).forEach(key => {
                    const newPrefix = prefix.slice(0);
                    newPrefix.push(key);
                    inverseMapping(newPrefix, currentMap[key]);
                });
            }
        };
        inverseMapping([], MapperService.settingsMappings);
        return map;
    }
}
